import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import practiceService from '../../services/practiceService';
import BloodPressure from './BloodPressure/BloodPressure';
import DailyTrendChart from './BloodPressure/Graphs/DailyTrendChart';
import MeanChart from './BloodPressure/Graphs/MeanChart';
import BloodGlucose from './BloodGlucose/BloodGlucose';
import DayChart from './BloodGlucose/Graphs/DayChart';
import MeanChartBG from './BloodGlucose/Graphs/MeanChart';
import Weight from './Weight/Weight';
import WeightGraphs from './Weight/Graphs/WeightGraphs';

class MeasurementErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || 'Unexpected rendering error' };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[MeasurementData] render error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, errorMessage: '' });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 16, margin: 12, border: '1px solid #f0b3b3', borderRadius: 8, background: '#fff7f7' }}>
          <strong>Unable to render measurement screen.</strong>
          <div style={{ marginTop: 6, fontSize: 13 }}>{this.state.errorMessage}</div>
          <button type="button" onClick={this.handleRetry} style={{ marginTop: 10 }}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const MeasurementData = () => {
  const { practiceId, patientId } = useParams();
  const [practiceIsLocked, setPracticeIsLocked] = useState(false);

  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    practiceService
      .getPractice(practiceId)
      .then((res) => {
        if (cancelled) return;
        const s = res?.data?.data?.status;
        const locked =
          s === 4 ||
          String(s).trim() === '4' ||
          String(s).toLowerCase() === 'locked' ||
          String(s).toLowerCase() === 'lost';
        setPracticeIsLocked(Boolean(locked));
      })
      .catch(() => {
        if (!cancelled) setPracticeIsLocked(false);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  return (
    <MeasurementErrorBoundary>
      <Routes>
        <Route
          path="blood-pressure"
          element={<BloodPressure practiceId={practiceId} patientId={patientId} practiceIsLocked={practiceIsLocked} />}
        />
        <Route
          path="blood-pressure/chart"
          element={<DailyTrendChart practiceId={practiceId} patientId={patientId} />}
        />
        <Route
          path="blood-pressure/chart/mean"
          element={<MeanChart practiceId={practiceId} patientId={patientId} />}
        />
        <Route
          path="blood-glucose"
          element={<BloodGlucose practiceId={practiceId} patientId={patientId} practiceIsLocked={practiceIsLocked} />}
        />
        <Route
          path="blood-glucose/chart"
          element={<DayChart practiceId={practiceId} patientId={patientId} />}
        />
        <Route
          path="blood-glucose/chart/mean"
          element={<MeanChartBG practiceId={practiceId} patientId={patientId} />}
        />
        <Route
          path="weight"
          element={<Weight practiceId={practiceId} patientId={patientId} practiceIsLocked={practiceIsLocked} />}
        />
        <Route
          path="weight/chart"
          element={<WeightGraphs />}
        />
        <Route
          path="*"
          element={<Navigate to="blood-pressure" replace />}
        />
      </Routes>
    </MeasurementErrorBoundary>
  );
};

export default MeasurementData;
